import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

function CompletedOrders() {
  const navigate = useNavigate();

  const [completedOrders, setCompletedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(10);

  // Filter states
  const [filterVisible, setFilterVisible] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  const pageRef = useRef(null);
  const tableRef = useRef(null);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    const role = localStorage.getItem("role");
    if (role !== "admin") navigate("/unauthorized");
  }, [navigate]);

  const fetchCompletedOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${ApiURl}/getCompletedOrders.php`);
      const data = await response.json();

      if (data?.success) {
        setCompletedOrders(data?.data || []);
        setError(null);
      } else {
        setError(data?.message || "Failed to fetch completed orders.");
      }
    } catch {
      setError("An error occurred while fetching data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompletedOrders();
    // Optional: Refresh every 30 seconds for real-time updates
    const intervalId = setInterval(fetchCompletedOrders, 30000);
    return () => clearInterval(intervalId);
  }, []);

  // Filter orders
  const filteredOrders = completedOrders.filter((order) => {
    let ok = true;

    // Date filter
    if (dateFrom) {
      const od = new Date(order.order_date);
      const df = new Date(dateFrom);
      if (od < df) ok = false;
    }

    if (dateTo) {
      const od = new Date(order.order_date);
      const dt = new Date(dateTo);
      dt.setHours(23, 59, 59, 999);
      if (od > dt) ok = false;
    }

    // Amount filter
    if (minAmount && parseFloat(order.total_amount) < parseFloat(minAmount)) {
      ok = false;
    }

    if (maxAmount && parseFloat(order.total_amount) > parseFloat(maxAmount)) {
      ok = false;
    }

    return ok;
  });

  // Pagination calculations
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const toggleFilterPanel = () => setFilterVisible((v) => !v);

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setMinAmount("");
    setMaxAmount("");
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setFilterVisible(false);
  };

  useEffect(() => {
    if (!pageRef.current) return;

    gsap.fromTo(
      pageRef.current,
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.45, ease: "power2.out" }
    );

    if (tableRef.current) {
      gsap.fromTo(
        tableRef.current,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, ease: "power2.out", delay: 0.12 }
      );
    }
  }, [loading]);

  const cardShell = "rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-300";

  const scrollTableLeft = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({ left: -320, behavior: "smooth" });
    }
  };

  const scrollTableRight = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({ left: 320, behavior: "smooth" });
    }
  };

  // Generate displayed page numbers
  const getDisplayedPages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  // Calculate total amount for filtered orders
  const totalAmount = filteredOrders.reduce((sum, order) => sum + parseFloat(order.total_amount), 0);

  return (
    <div ref={pageRef} className="min-h-screen bg-white p-4 sm:p-6 font-['Poppins',sans-serif]">
      {/* Header */}
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            Completed Orders
          </h1>
          <p className="text-sm text-gray-500">
            View all completed and delivered orders with detailed information.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchCompletedOrders}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition"
          >
            Refresh
          </button>

          <button
            onClick={toggleFilterPanel}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition"
          >
            Filter Orders
          </button>

          <Link
            to="/admin/dashboard"
            className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 transition"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Stats Card */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${cardShell} p-5 bg-gradient-to-br from-emerald-100 to-emerald-50`}>
          <p className="text-xs font-semibold text-gray-600">Total Completed Orders</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {loading ? (
              <span className="inline-block animate-pulse">Loading…</span>
            ) : error ? (
              <span className="text-rose-600">Error</span>
            ) : (
              filteredOrders.length
            )}
          </p>
          <p className="mt-1 text-xs text-gray-500">All delivered orders</p>
        </div>

        <div className={`${cardShell} p-5 bg-gradient-to-br from-blue-100 to-blue-50`}>
          <p className="text-xs font-semibold text-gray-600">Total Revenue</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {loading ? (
              <span className="inline-block animate-pulse">Loading…</span>
            ) : error ? (
              <span className="text-rose-600">Error</span>
            ) : (
              `₹${totalAmount.toLocaleString('en-IN')}`
            )}
          </p>
          <p className="mt-1 text-xs text-gray-500">From completed orders</p>
        </div>

        <div className={`${cardShell} p-5 bg-gradient-to-br from-amber-100 to-amber-50`}>
          <p className="text-xs font-semibold text-gray-600">Showing</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {loading ? (
              <span className="inline-block animate-pulse">Loading…</span>
            ) : error ? (
              <span className="text-rose-600">Error</span>
            ) : (
              currentOrders.length
            )}
          </p>
          <p className="mt-1 text-xs text-gray-500">Orders on this page</p>
        </div>
      </div>

      {/* FILTER PANEL */}
      {filterVisible && (
        <>
          <div 
            className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-[2px]" 
            onClick={() => setFilterVisible(false)}
          />
          
          <div className="fixed inset-0 z-[101] flex items-start justify-center pt-20 px-4">
            <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white shadow-2xl p-4">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-900">Filter Orders</h3>
                <button
                  onClick={() => setFilterVisible(false)}
                  className="text-gray-400 hover:text-gray-700 text-xl"
                  aria-label="Close filters"
                >
                  ×
                </button>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Date From
                    </label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Date To
                    </label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Min Amount (₹)
                    </label>
                    <input
                      type="number"
                      value={minAmount}
                      onChange={(e) => setMinAmount(e.target.value)}
                      placeholder="100"
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Max Amount (₹)
                    </label>
                    <input
                      type="number"
                      value={maxAmount}
                      onChange={(e) => setMaxAmount(e.target.value)}
                      placeholder="10000"
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={resetFilters}
                    className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition"
                  >
                    Reset
                  </button>
                  <button
                    onClick={applyFilters}
                    className="flex-1 rounded-xl bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition"
                  >
                    Apply Filters
                  </button>
                </div>

                <p className="text-[11px] text-gray-500">
                  {filteredOrders.length} orders match current filters
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* TABLE */}
      <div className={`${cardShell} p-5`} ref={tableRef}>
        <div className="flex items-start sm:items-center justify-between gap-3 mb-4 flex-col sm:flex-row">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Completed Orders List</h3>
            <p className="text-xs text-gray-500">
              Page {currentPage} of {totalPages} • {filteredOrders.length} total orders
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <select
                value={ordersPerPage}
                onChange={(e) => {
                  setOrdersPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={scrollTableLeft}
                className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-3 py-2 text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-200 transition"
                aria-label="Scroll table left"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                type="button"
                onClick={scrollTableRight}
                className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-3 py-2 text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-200 transition"
                aria-label="Scroll table right"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div
          ref={tableContainerRef}
          className="overflow-x-auto rounded-2xl border border-gray-100 scroll-smooth overscroll-x-contain"
          style={{
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-x",
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e1 #f1f5f9",
          }}
        >
          <table className="min-w-[980px] w-full table-auto bg-white">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                  Order No
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                  Date & Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                  Invoice No
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                  Items
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-gray-500" colSpan={7}>
                    <span className="animate-pulse">Loading completed orders…</span>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-rose-600" colSpan={7}>
                    {error}
                  </td>
                </tr>
              ) : currentOrders.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-gray-500" colSpan={7}>
                    No completed orders found. {dateFrom || dateTo || minAmount || maxAmount ? "Try changing your filters." : ""}
                  </td>
                </tr>
              ) : (
                currentOrders.map((order) => (
                  <tr
                    key={order.order_id}
                    className="border-b border-gray-50 hover:bg-gray-50/70 transition"
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      #{order.order_id}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      {order.full_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {order.order_date}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {order.invoice_number || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-emerald-700">
                      ₹{order.total_amount}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {order.item_count || "1"} items
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() =>
                            navigate(`/admin/AdminViewDetails/${order.order_id}`)
                          }
                          className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition whitespace-nowrap"
                        >
                          View Details
                        </button>

                        <button
                          onClick={() =>
                            navigate(`/admin/AdminReceipt/${order.order_id}`)
                          }
                          className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 transition whitespace-nowrap"
                        >
                          View Bill
                        </button>

                        {order.review_id && (
                          <button
                            onClick={() => navigate(`/admin/review/${order.order_id}`)}
                            className="rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700 transition whitespace-nowrap"
                          >
                            Review
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredOrders.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, filteredOrders.length)} of{" "}
              {filteredOrders.length} entries
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {getDisplayedPages().map((pageNum, index) =>
                  pageNum === "..." ? (
                    <span key={`dots-${index}`} className="px-3 py-2 text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                        currentPage === pageNum
                          ? "bg-emerald-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                )}
              </div>

              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Next
              </button>
            </div>

            <div className="sm:hidden">
              <select
                value={ordersPerPage}
                onChange={(e) => {
                  setOrdersPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>
        )}

        <div className="mt-4 text-xs text-gray-500 lg:hidden">
          Tip: Swipe left/right on the table to view all columns.
        </div>
      </div>
    </div>
  );
}

export default CompletedOrders;